import { ErrorManagement } from '@printdeal/error-management/react';
import Api from '../Api';
import { AccountSurveyData } from '../../components/my-account/components/widgets/surveys/types/survey-types';
import { FeedbackFormValues } from '../../components/customer-programs/conversation-loop/helpers/conversation-loop';
import { AnonymousTokenNotAllowedError } from '../../errors/AnonymousTokenNotAllowedError';
import { CollectiveInvoicingPayload, FrequencyOption } from '../../components/customer-programs/InvoiceCollective/helpers/types';

export type Customer = {
  id: string;
  vatId?: string;
  customerNumber: string;
  email: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  customerGroup: CustomerGroup;
  customerGroupAssignments: any[];
  custom: CustomerCustom;
};

type CustomerCustom = {
  fields: CustomerCustomFields;
};

type CustomerCustomFields = {
  isApiUser?: boolean;
  isCompany?: boolean;
  cocNumber?: string;
  collectiveInvoiceInterval?: FrequencyOption;
};

type CustomerGroup = {
  id: string;
};

export type Account = {
  id: string;
  firstName: string;
  lastName: string;
  locale: string;
  email: string;
  newsletterOptIn: boolean;
  gender?: string;
  surveyDetails?: any[]; // TODO: Type this properly based on your survey types
};

interface IEnrichUser {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  dateOfBirth: string;
  isCompany: string;
  company: string;
  kvkNumber: string;
  vatId: string;
  streetName: string;
  streetNumber: string;
  additionalStreetInfo: string;
  postalCode: string;
  city: string;
  country: string;
  isSubscribed: boolean;
}

export type PartialAccount = Partial<Account> & {
  newPassword?: string;
  currentPassword?: string;
};

class CustomerService extends Api {
  async getCustomer(): Promise<Customer> {
    try {
      const { data } = await this.httpClient.get<Customer>(
        '/customer/getCustomer',
        { blockAnonymous: true },
      );
      return data;
    } catch (error) {
      if (!(error instanceof AnonymousTokenNotAllowedError)) {
        ErrorManagement.exception({
          exception: error,
          tags: {
            fileLocation: 'CustomerService',
            featureFunction: 'getCustomer',
          },
          fingerprint: ['customer-service-get-customer'],
        });
      }
      throw error;
    }
  }

  async getAccount(): Promise<Account> {
    try {
      const { data } = await this.httpClient.get<Account>(
        '/customer/getAccount',
        { blockAnonymous: true },
      );
      return data;
    } catch (error) {
      if (!(error instanceof AnonymousTokenNotAllowedError)) {
        ErrorManagement.exception({
          exception: error,
          tags: {
            fileLocation: 'CustomerService',
            featureFunction: 'getAccount',
          },
          fingerprint: ['customer-service-get-account'],
        });
      }
      throw error;
    }
  }

  async updateAccount(account: PartialAccount) {
    const { data } = await this.httpClient
      .post('/customer/updateAccountDetails', account)
      .catch(this.errorHandler);
    return Promise.resolve(data);
  }

  async addCustomerToResellerGroup() {
    const { data } = await this.httpClient
      .post('/customer/addCustomerToResellerGroup')
      .catch(this.errorHandler);
    return Promise.resolve(data);
  }

  async authorizePrintdealAPI(redirectUri: string, authenticationId: string) {
    try {
      const data = await this.httpClient.post(
        '/customer/authorizeCustomer',
        {
          redirectUri,
          authenticationId,
        },
      );
      return data;
    } catch (error) {
      // @ts-expect-error
      const { response } = error;
      return response;
    }
  }

  async enrichRegistration(payload: IEnrichUser) {
    const { kvkNumber, ...rest } = payload;
    const data = await this.httpClient.post(
      '/customer/register',
      {
        ...rest,
        cocNumber: kvkNumber,
      },
    );
    return Promise.resolve(data);
  }

  async addSurveyAnswersToCustomer(accountSurveyData: AccountSurveyData) {
    const { data } = await this.httpClient.post(
      '/customer/updateSurveyData',
      {
        ...accountSurveyData,
      },
    );
    return data.account;
  }

  async addConversationToCustomer(conversationAppData: FeedbackFormValues) {
    const { data } = await this.httpClient.post(
      '/customer/storeConversationData',
      {
        ...conversationAppData,
      },
    );
    return data.customer;
  }

  async storeCollectiveInvoicingData(collectiveInvoivingData: CollectiveInvoicingPayload) {
    const { data } = await this.httpClient.post(
      '/customer/collective-invoicing',
      {
        ...collectiveInvoivingData,
      },
    );
    return data;
  }
}

export default CustomerService;
